import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import { Add, GetApp } from "@material-ui/icons";
import Loading from "components/Loading";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import { Dispatch, SetStateAction, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import {
  b64toBlob,
  exportVehicleInfo,
  listVehicleInfo,
  saveVehicleInfo,
} from "shared/network/vehicle-info.api";
import { Vehicle } from "shared/types";
import VehicleInfoDialogRow from "./VehicleInfoDialogRow";

export type Props = {
  vehicleInfoOpen: boolean;
  setVehicleInfoOpen: Dispatch<SetStateAction<boolean>>;
  date: Date;
  vehicles: Vehicle[];
  refetch: () => void;
};

export type FormValues = {
  rows: {
    id: number;
    vehicleId: number;
    tourLength: number;
    link: string;
    deliveryBasePrice: number;
    KMExpense: number;
    note1: string;
    note2: string;
    note3: string;
  }[];
};

const VehicleInfoDialog = ({
  vehicleInfoOpen,
  setVehicleInfoOpen,
  date,
  vehicles,
  refetch,
}: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const form = useForm<FormValues>();
  const { control, handleSubmit } = form;
  const [removed, setRemoved] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isChange, setIsChange] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);

  const { append, fields, remove } = useFieldArray({
    control,
    name: "rows",
    keyName: "key",
  });

  const listVehicleInfoQuery = useQuery(
    "listVehicleInfoQuery",
    async () => {
      const { data } = await listVehicleInfo(format(date, "yyyy-MM-dd"));
      return data?.items;
    },
    {
      onSuccess: data => {
        data.forEach(value => append({ vehicleId: value?.vehicle?.id, ...value }));
      },
    },
  );

  async function onSubmit(values: FormValues) {
    setLoading(true);
    try {
      await saveVehicleInfo([
        ...(values?.rows || [])?.map((value, index) => {
          return {
            ...value,
            deliveryDate: format(date, "yyyy-MM-dd"),
            id: value?.id || null,
            vehicle: vehicles?.find(vehicle => vehicle?.id === Number(value?.vehicleId)),
            isDelete: false,
            note1: value.note1,
            note2: value.note2,
            note3: value.note3,
          };
        }),
        ...removed,
      ]);
      setVehicleInfoOpen(false);
      refetch?.();
      enqueueSnackbar(t("common:notification.save.success"), { variant: "success" });
    } catch (error) {
      enqueueSnackbar(t("common:notification.save.failure"), { variant: "error" });
    }
    setLoading(false);
  }

  async function handleExport() {
    try {
      setExportLoading(true);
      const response = await exportVehicleInfo({ param: format(date, "yyyy-MM-dd") });
      const data = response.data.item.content;

      const blob = b64toBlob(data);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      link.setAttribute("download", `vehicle_info_${format(date, "yyyy-MM-dd")}.xlsx`);

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error in export:", error);
    } finally {
      setExportLoading(false);
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth={"md"}
      open={vehicleInfoOpen}
      onClose={() => setVehicleInfoOpen(false)}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          padding: 8,
        }}
      >
        <DialogTitle>{t("vehicleInfo.dialogTitle")}</DialogTitle>
        <Button
          variant="outlined"
          size="small"
          style={{ height: 40 }}
          disabled={exportLoading}
          startIcon={exportLoading ? <CircularProgress size={20} /> : <GetApp />}
          onClick={() => handleExport()}
        >
          {t("common:button.export")}
        </Button>
      </Box>
      {listVehicleInfoQuery?.isFetching ? (
        <Loading />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent>
              {fields?.map((field, index) => {
                return (
                  <VehicleInfoDialogRow
                    key={field.key}
                    field={field}
                    index={index}
                    remove={remove}
                    setRemoved={setRemoved}
                    vehicles={vehicles}
                    isChange={isChange}
                    setIsChange={setIsChange}
                  />
                );
              })}
              <Box display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  disabled={fields?.length === vehicles?.length}
                  startIcon={<Add />}
                  onClick={() => append({ vehicleId: "", tourLength: "", link: "" })}
                >
                  {t("common:button.add")}
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setVehicleInfoOpen(false)}>{t("common:button.cancel")}</Button>
              <Button
                disabled={loading || (!removed?.length && fields?.length === 0)}
                type="submit"
                variant="contained"
                color="primary"
              >
                {loading ? <CircularProgress size={24} /> : t("common:button.save")}
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      )}
    </Dialog>
  );
};

export default VehicleInfoDialog;
